import { ownerDocument } from '../../../../../utils/dom';

import type * as React from 'react';

import type { Mark, SliderProps, SliderRangeValue, SliderValue } from '../../slider/slider.types';

/**
 * Number of pointer-move events that must fire before a drag is considered intentional.
 * Prevents accidental drags from triggering the dragging visual state on click.
 */
export const INTENTIONAL_DRAG_COUNT_THRESHOLD = 2;

/**
 * CSS property mappings for each slider axis orientation.
 * Each axis defines how to position an element (`offset`) and size the track (`leap`)
 * using the appropriate CSS properties for that direction.
 */
export const AXIS_PROPS = {
  horizontal: {
    leap: (percent: number) => ({ width: `${percent}%` }),
    offset: (percent: number) => ({ left: `${percent}%` }),
  },
  'horizontal-reverse': {
    leap: (percent: number) => ({ width: `${percent}%` }),
    offset: (percent: number) => ({ right: `${percent}%` }),
  },
  vertical: {
    leap: (percent: number) => ({ height: `${percent}%` }),
    offset: (percent: number) => ({ bottom: `${percent}%` }),
  },
} as const;

/** Identity function used as the default `scale` transform. */
export const identity = (value: number): number => value;

const DEFAULT_SHIFT_STEP = 10;
const DEFAULT_STEP = 1;

const warnInvalidSliderConfiguration = (message: string): void => {
  if (process.env.NODE_ENV !== 'production') {
    console.warn(`Slider: ${message}`);
  }
};

/**
 * Clamps a value between a minimum and maximum bound.
 *
 * @param value - The value to clamp.
 * @param min - Lower bound (defaults to `Number.MIN_SAFE_INTEGER`).
 * @param max - Upper bound (defaults to `Number.MAX_SAFE_INTEGER`).
 * @returns The clamped value.
 */
export const clamp = (
  value: number,
  min: number = Number.MIN_SAFE_INTEGER,
  max: number = Number.MAX_SAFE_INTEGER,
): number => {
  return Math.max(min, Math.min(value, max));
};

/** Comparator for sorting numbers in ascending order. */
export const asc = (a: number, b: number): number => {
  return a - b;
};

/**
 * Finds the index of the value closest to `currentValue` in a sorted array.
 * When two values are equidistant, the later index wins (useful for range sliders
 * so that the higher thumb is preferred when clicking exactly between two thumbs).
 *
 * @param values - Array of numeric values to search.
 * @param currentValue - The target value to find the closest match for.
 * @returns The index of the closest value.
 */
export const findClosest = (values: number[], currentValue: number): number => {
  const closest = values.reduce<null | { distance: number; index: number }>((acc, value, index) => {
    const distance = Math.abs(currentValue - value);

    if (acc == null || distance < acc.distance || distance === acc.distance) {
      return { distance, index };
    }

    return acc;
  }, null);

  return closest?.index ?? 0;
};

/**
 * Normalizes the `step` prop to a safe runtime value.
 *
 * - `step={null}` is only valid when an explicit non-empty `marks` array is provided.
 * - Non-positive or non-finite values fall back to `1`.
 */
export const normalizeStep = (step: null | number, marks: boolean | ReadonlyArray<Mark>): null | number => {
  if (step === null) {
    if (Array.isArray(marks) && marks.length > 0) {
      return null;
    }

    warnInvalidSliderConfiguration(
      marks === true
        ? '`marks={true}` requires a positive numeric `step`. Falling back to `step={1}`.'
        : '`step={null}` requires an explicit non-empty `marks` array. Falling back to `step={1}`.',
    );

    return DEFAULT_STEP;
  }

  if (!Number.isFinite(step) || step <= 0) {
    warnInvalidSliderConfiguration('`step` must be a positive finite number. Falling back to `step={1}`.');
    return DEFAULT_STEP;
  }

  return step;
};

/**
 * Normalizes `shiftStep` to a positive finite number.
 */
export const normalizeShiftStep = (shiftStep: number): number => {
  if (!Number.isFinite(shiftStep) || shiftStep <= 0) {
    warnInvalidSliderConfiguration(
      `\`shiftStep\` must be a positive finite number. Falling back to \`${DEFAULT_SHIFT_STEP}\`.`,
    );
    return DEFAULT_SHIFT_STEP;
  }

  return shiftStep;
};

/**
 * Normalizes `minDistance` to a non-negative finite number.
 */
export const normalizeMinDistance = (minDistance: number): number => {
  if (!Number.isFinite(minDistance) || minDistance < 0) {
    warnInvalidSliderConfiguration('`minDistance` must be a non-negative finite number. Falling back to `0`.');
    return 0;
  }

  return minDistance;
};

/**
 * Normalizes `snapToMarks` to a safe runtime value.
 */
export const normalizeSnapToMarks = (snapToMarks: SliderProps['snapToMarks']): SliderProps['snapToMarks'] => {
  if (typeof snapToMarks !== 'number') {
    return snapToMarks ?? false;
  }

  if (!Number.isFinite(snapToMarks) || snapToMarks < 0) {
    warnInvalidSliderConfiguration(
      '`snapToMarks` must be `true`, `false`, or a non-negative finite number. Disabling snapping.',
    );
    return false;
  }

  return snapToMarks;
};

/**
 * Resolves track variants that are not supported for the current slider shape.
 * Center-origin track rendering only works for single-thumb sliders.
 */
export const resolveTrackMode = (track: SliderProps['track'], range: boolean): SliderProps['track'] => {
  if (track === 'center' && range) {
    warnInvalidSliderConfiguration(
      '`track="center"` is only supported for single-value sliders. Falling back to `track="normal"`.',
    );
    return 'normal';
  }

  return track;
};

/**
 * Extracts the (x, y) coordinates of a pointer from a mouse or touch event.
 * For touch events, it matches the finger by `touchId` to support multi-touch correctly.
 *
 * @param event - The mouse or touch event.
 * @param touchId - Ref holding the tracked touch identifier.
 * @returns The finger coordinates, or `false` if the tracked touch was not found.
 */
export const trackFinger = (
  event: MouseEvent | React.MouseEvent | TouchEvent,
  touchId: React.RefObject<number | undefined>,
): false | { x: number; y: number } => {
  if ('changedTouches' in event) {
    const touch =
      touchId.current === undefined
        ? event.changedTouches[0]
        : [...event.changedTouches].find((changedTouch) => changedTouch.identifier === touchId.current);

    if (!touch) {
      return false;
    }

    return { x: touch.clientX, y: touch.clientY };
  }

  return {
    x: event.clientX,
    y: event.clientY,
  };
};

/**
 * Converts a percentage (0–1) back to an absolute value within the slider range.
 *
 * @param percent - A value between 0 and 1 representing position on the track.
 * @param min - The slider minimum.
 * @param max - The slider maximum.
 * @returns The corresponding absolute value.
 */
export const percentToValue = (percent: number, min: number, max: number): number => {
  return (max - min) * percent + min;
};

/**
 * Determines the number of decimal digits needed to represent a number without
 * floating-point rounding artifacts. Handles small numbers expressed in
 * scientific notation (e.g. `0.001` → `1e-3` → 3 decimal places).
 *
 * @param num - The number to inspect.
 * @returns The decimal precision (number of digits after the decimal point).
 */
export const getDecimalPrecision = (num: number): number => {
  if (num === 0) return 0;

  if (Math.abs(num) < 1) {
    const parts = num.toExponential().split('e-');
    const mantissaDecimalPart = parts[0]?.split('.')[1];

    return (mantissaDecimalPart ? mantissaDecimalPart.length : 0) + Number.parseInt(parts[1] ?? '0', 10);
  }

  const decimalPart = num.toString().split('.')[1];
  return decimalPart ? decimalPart.length : 0;
};

/**
 * Rounds a value to the nearest step increment relative to `min`.
 * Uses `getDecimalPrecision` to avoid floating-point rounding errors
 * (e.g. `0.1 + 0.2 !== 0.3`).
 *
 * @param value - The raw value to round.
 * @param step - The step increment.
 * @param min - The slider minimum (the step grid origin).
 * @returns The value snapped to the nearest step.
 */
export const roundValueToStep = (value: number, step: number, min: number): number => {
  const nearest = Math.round((value - min) / step) * step + min;
  return Number(nearest.toFixed(getDecimalPrecision(step)));
};

/**
 * Returns a new sorted array with the value at `index` replaced by `newValue`.
 * Used to update one thumb in a range slider while keeping the array sorted.
 *
 * @param values - The current thumb values.
 * @param newValue - The new value for the thumb at `index`.
 * @param index - The thumb index to update.
 * @returns A new sorted array of thumb values.
 */
export const setValueIndex = (values: number[], newValue: number, index: number): number[] => {
  const output = [...values];
  output[index] = newValue;
  return output.toSorted(asc);
};

const normalizeSingleValue = (value: number, min: number, max: number, marks: Mark[], step: null | number): number => {
  const safeValue = Number.isFinite(value) ? value : min;
  const clampedValue = clamp(safeValue, min, max);

  if (step === null && marks.length > 0) {
    return (
      marks[
        findClosest(
          marks.map((mark) => mark.value),
          clampedValue,
        )
      ]?.value ?? min
    );
  }

  return clampedValue;
};

const normalizeRangeValueShape = (values: number[], min: number): [number, number] => {
  if (values.length === 2) {
    return [values[0] ?? min, values[1] ?? min];
  }

  if (values.length === 0) {
    warnInvalidSliderConfiguration('Range values must contain exactly two items. Falling back to `[min, min]`.');
    return [min, min];
  }

  if (values.length === 1) {
    warnInvalidSliderConfiguration(
      'Range values must contain exactly two items. Duplicating the provided value for the second thumb.',
    );
    return [values[0] ?? min, values[0] ?? min];
  }

  warnInvalidSliderConfiguration('Range values must contain exactly two items. Using the first two provided values.');
  return [values[0] ?? min, values[1] ?? min];
};

/**
 * Normalizes initial range values so they satisfy `minDistance` on first render.
 * When the requested minimum gap cannot fit inside the slider range, the values
 * are spread evenly across the available range.
 */
export const normalizeInitialRangeValues = (
  values: number[],
  {
    max = Number.MAX_SAFE_INTEGER,
    min = Number.MIN_SAFE_INTEGER,
    minDistance = 0,
  }: {
    max?: number;
    min?: number;
    minDistance?: number;
  },
): number[] => {
  const nextValues = values.toSorted(asc).map((value) => clamp(value, min, max));

  if (nextValues.length <= 1 || minDistance <= 0) {
    return nextValues;
  }

  const rangeSize = max - min;

  if (rangeSize <= 0) {
    return nextValues;
  }

  const requiredSpan = minDistance * (nextValues.length - 1);

  if (requiredSpan > rangeSize) {
    warnInvalidSliderConfiguration(
      '`minDistance` is larger than the available slider range for the current number of thumbs. Falling back to maximally spaced values.',
    );

    const fallbackStep = rangeSize / (nextValues.length - 1);

    return nextValues.map((_, index) => min + fallbackStep * index);
  }

  nextValues[0] = clamp(nextValues[0] ?? min, min, max - requiredSpan);

  for (let index = 1; index < nextValues.length; index += 1) {
    const previousValue = nextValues[index - 1] ?? min;
    nextValues[index] = Math.max(nextValues[index] ?? min, previousValue + minDistance);
  }

  const overshoot = (nextValues.at(-1) ?? min) - max;

  if (overshoot > 0) {
    for (let index = 0; index < nextValues.length; index += 1) {
      nextValues[index] = (nextValues[index] ?? min) - overshoot;
    }
  }

  return nextValues.map((value) => clamp(value, min, max));
};

/**
 * Normalizes the initial slider value so the component never mounts in an
 * invalid state. Marks-only sliders snap to the nearest mark, range values are
 * coerced to exactly two thumbs, and initial range values are adjusted to
 * satisfy `minDistance`.
 */
export const normalizeSliderValue = (
  value: SliderValue | undefined,
  {
    marks,
    max = Number.MAX_SAFE_INTEGER,
    min = Number.MIN_SAFE_INTEGER,
    minDistance = 0,
    step = DEFAULT_STEP,
  }: {
    marks: Mark[];
    max?: number;
    min?: number;
    minDistance?: number;
    step?: null | number;
  },
): SliderValue | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (!Array.isArray(value)) {
    return normalizeSingleValue(value, min, max, marks, step);
  }

  const normalizedRangeValue = normalizeRangeValueShape(value, min);

  return normalizeInitialRangeValues(
    normalizedRangeValue.map((item) => normalizeSingleValue(item, min, max, marks, step)),
    { max, min, minDistance },
  ) as [number, number];
};

/**
 * Logs a console warning in development if `min >= max`, which would produce
 * a broken slider with zero or negative range.
 */
export const validateSliderRange = (min: number, max: number): void => {
  if (process.env.NODE_ENV !== 'production' && min >= max) {
    console.warn(`Slider: \`min\` (${min}) must be less than \`max\` (${max}).`);
  }
};

/*
 * Constrains a new thumb value within a range slider so that thumbs don't
 * violate `minDistance` or swap past each other (when `disableSwap` is set).
 *
 * When swapping is allowed and no minimum distance is enforced, the thumb may
 * freely cross other thumbs; the returned `activeIndex` is updated to reflect
 * the thumb's new sorted position.
 *
 * - `values` — current sorted array of thumb values.
 * - `newValue` — the proposed new value for the thumb at `index`.
 * - `index` — the index of the thumb being moved.
 * - 4th arg — constraint options (`disableSwap`, `min`, `max`, `minDistance`).
 *
 * Returns the constrained value array and the (possibly updated) active thumb index.
 */
export const constrainRangeValue = (
  values: number[],
  newValue: number,
  index: number,
  {
    disableSwap = false,
    max = Number.MAX_SAFE_INTEGER,
    min = Number.MIN_SAFE_INTEGER,
    minDistance = 0,
  }: {
    disableSwap?: boolean;
    max?: number;
    min?: number;
    minDistance?: number;
  },
): { activeIndex: number; value: number[] } => {
  const safeMinDistance = Math.max(minDistance, 0);
  const shouldPreventSwap = disableSwap || safeMinDistance > 0;
  let nextValue = clamp(newValue, min, max);

  if (shouldPreventSwap) {
    const lowerBound = index > 0 ? values[index - 1] + safeMinDistance : min;
    const upperBound = index < values.length - 1 ? values[index + 1] - safeMinDistance : max;

    if (lowerBound <= upperBound) {
      nextValue = clamp(nextValue, lowerBound, upperBound);
    } else {
      // Adjacent thumbs are closer than minDistance — constraints are impossible.
      // Keep the thumb at its current position to avoid violating minDistance or swapping.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Slider: `minDistance` is larger than the gap between adjacent thumbs. The move was discarded.');
      }

      nextValue = clamp(values[index] ?? nextValue, min, max);
    }

    return {
      activeIndex: index,
      value: setValueIndex(values, nextValue, index),
    };
  }

  const previousValue = nextValue;
  const value = setValueIndex(values, nextValue, index);

  return {
    activeIndex: value.indexOf(previousValue),
    value,
  };
};

/**
 * Produces a new slider value with one thumb updated. For single-value sliders
 * it simply returns the new number; for range sliders it returns a new array.
 *
 * @param currentValue - The current slider value (number or number[]).
 * @param thumbIndex - Index of the thumb to update.
 * @param nextValue - The new value for the thumb.
 * @returns The updated slider value.
 */
export const updateThumbValue = (currentValue: SliderValue, thumbIndex: number, nextValue: number): SliderValue => {
  if (!Array.isArray(currentValue)) {
    return nextValue;
  }

  const nextValues = [...currentValue];
  nextValues[thumbIndex] = nextValue;
  return nextValues as SliderRangeValue;
};

/*
 * Programmatically focuses the hidden `<input type="range">` for the thumb at
 * `activeIndex`. This is needed after pointer interactions so keyboard navigation
 * continues from the correct thumb.
 *
 * - `activeIndex` — the thumb index to focus.
 * - `setActive` — optional setter to mark the thumb as active (used during drag).
 * - `sliderRef` — ref to the slider root element that contains the hidden inputs.
 */
export const focusThumb = ({
  activeIndex,
  setActive,
  sliderRef,
}: {
  activeIndex: number;
  setActive?: (index: number) => void;
  sliderRef: React.RefObject<HTMLSpanElement | null>;
}): void => {
  const doc = ownerDocument(sliderRef.current);
  const activeElement = doc.activeElement;
  const focusedIndex = activeElement instanceof HTMLElement ? Number(activeElement.dataset.index) : Number.NaN;

  if (!sliderRef.current?.contains(activeElement) || focusedIndex !== activeIndex) {
    sliderRef.current?.querySelector<HTMLInputElement>(`[type="range"][data-index="${activeIndex}"]`)?.focus();
  }

  setActive?.(activeIndex);
};

/**
 * Shallow-compares two arrays element by element.
 *
 * @param array1 - First array.
 * @param array2 - Second array.
 * @param itemComparer - Optional comparator (defaults to strict equality).
 * @returns `true` if both arrays have the same length and all elements match.
 */
export const areArraysEqual = <Item>(
  array1: ReadonlyArray<Item>,
  array2: ReadonlyArray<Item>,
  itemComparer: (a: Item, b: Item) => boolean = (a, b) => a === b,
): boolean => {
  return array1.length === array2.length && array1.every((value, index) => itemComparer(value, array2[index]));
};

/**
 * Compares two slider values for equality. Handles both single-value (number)
 * and range (number[]) slider variants.
 *
 * @param newValue - The new slider value.
 * @param oldValue - The previous slider value.
 * @returns `true` if the values are semantically equal.
 */
export const areValuesEqual = (newValue: number | number[], oldValue: number | ReadonlyArray<number>): boolean => {
  if (typeof newValue === 'number' && typeof oldValue === 'number') {
    return newValue === oldValue;
  }

  if (Array.isArray(newValue) && Array.isArray(oldValue)) {
    return areArraysEqual(newValue, oldValue);
  }

  return false;
};

/**
 * Creates a cloned native Event with a synthetic `target` containing the slider's
 * `name` and current `value`. This mimics the behavior of native `<input>` change
 * events so that form libraries (e.g. Formik, React Hook Form) can read the value
 * from `event.target.value`.
 *
 * @param event - The original browser or React event that triggered the change.
 * @param value - The new slider value.
 * @param name - The slider's `name` attribute.
 * @returns A cloned Event with `target.value` and `target.name` set.
 */
export const createChangeEvent = (
  event: Event | React.SyntheticEvent,
  value: number | number[],
  name?: string,
): Event => {
  const nativeEvent = ('nativeEvent' in event ? event.nativeEvent : event) as Event;
  const EventConstructor = nativeEvent.constructor as new (type: string, init?: EventInit) => Event;

  let clonedEvent: Event;

  try {
    clonedEvent = new EventConstructor(nativeEvent.type, nativeEvent as unknown as EventInit);
  } catch {
    clonedEvent = new Event(nativeEvent.type, {
      bubbles: nativeEvent.bubbles,
      cancelable: nativeEvent.cancelable,
    });
  }

  Object.defineProperty(clonedEvent, 'target', {
    configurable: true,
    value: { name, value },
    writable: true,
  });

  return clonedEvent;
};

/** Cached result of the `touch-action: none` CSS support check. */
let cachedSupportsTouchActionNone: boolean | undefined;

/**
 * Detects whether the browser supports `touch-action: none`.
 * The result is cached after the first call for performance.
 *
 * When supported, touch events can use `passive: true` listeners because
 * `touch-action: none` on the element already prevents scrolling.
 * When not supported, touch handlers must call `preventDefault()` manually.
 */
export const doesSupportTouchActionNone = (): boolean => {
  if (cachedSupportsTouchActionNone === undefined) {
    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
      cachedSupportsTouchActionNone = CSS.supports('touch-action', 'none');
    } else {
      cachedSupportsTouchActionNone = true;
    }
  }

  return cachedSupportsTouchActionNone;
};
